/**
 * Extract an HTTP-style status number from a thrown value.
 *
 * Conventions used across the codebase (both supported):
 * - **Service layer** throws `new Error(msg, { cause: status })` — cause is the number.
 * - **Core layer** (`getApiError`) throws `new Error(msg, { cause: { status } })` —
 *   cause is an object containing the status, because page-level catches
 *   destructure as `(err as { cause?: { status?: number } })?.cause?.status`.
 *
 * Plus: `AbortError` from fetch cancellation maps to `499` (Nginx's "Client Closed
 * Request"), and anything else is unexpected — `console.error`'d and returns
 * `undefined` so callers can branch on "no known status".
 *
 * Extracted from a previously-duplicated local helper in every `src/core/services/*`
 * file. Accepts both cause shapes so a getApiError-thrown error can be safely
 * passed through this helper too.
 */
export const getErrorStatus = (error: unknown): number | undefined => {
  if (error instanceof DOMException && error.name === "AbortError") return 499;
  if (error instanceof Error) {
    if (typeof error.cause === "number") return error.cause;
    if (error.cause !== null && typeof error.cause === "object" && "status" in error.cause) {
      const status = (error.cause as { status?: unknown }).status;
      if (typeof status === "number") return status;
    }
  }
  console.error(error);
  return undefined;
};
